/**
 * Billing context: the active org's `org_billing` row (fetched via the
 * `reconcile_seat_grace` RPC so grace state self-heals), the derived grace
 * state, trial extension, and the trial-acknowledge flow.
 *
 * Split out of the legacy monolithic `OrgProvider` (audit item 26). Nests
 * inside `OrgCoreProvider` and reads `activeOrg` / `refreshCore` /
 * `clearJustActivatedTrial` from it.
 *
 * `billingRefreshKey` is a monotonic signal that downstream billing hooks
 * (`useOrgCredits`, `usePlanBadge`, `useTrialStatus`) include in their effect
 * deps so they refetch their Stripe-backed data even when the active org id
 * hasn't changed. It is bumped on trial activation, extension, and
 * acknowledgement.
 *
 * @module lib/BillingProvider
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSupabase } from './SupabaseProvider';
import { useOrgCore } from './OrgCoreProvider';
import { captureException } from './telemetry/posthog/frontend';
import type { OrgBilling, GraceState } from './org-types';

/**
 * Thrown when the user tries to extend a trial that is not currently trialing.
 * Maps to HTTP 409 `{error: "trial_not_active"}`.
 */
export class TrialNotActiveError extends Error {
  constructor(message = 'No active Pro trial to extend.') {
    super(message);
    this.name = 'TrialNotActiveError';
  }
}

/**
 * Thrown when the server refuses to extend because the trial has already been
 * extended. Maps to HTTP 409 `{error: "already_extended"}`.
 */
export class TrialAlreadyExtendedError extends Error {
  constructor(message = 'This trial has already been extended.') {
    super(message);
    this.name = 'TrialAlreadyExtendedError';
  }
}

/**
 * Generic failure extending a trial: network error, Stripe outage, etc.
 * Callers should surface a generic retry message.
 */
export class ExtendTrialError extends Error {
  constructor(message = 'Could not extend trial. Please try again.') {
    super(message);
    this.name = 'ExtendTrialError';
  }
}

/** Shape of the billing context value. */
export interface BillingContextType {
  /** Billing record for the active org, or null. */
  orgBilling: OrgBilling | null;
  /** Whether the active org's billing row is still being fetched. */
  billingLoading: boolean;
  /** Grace period state derived from the active org's billing row. */
  graceState: GraceState;
  /** Re-fetch billing for the active org via the reconcile RPC. No-op when no org. */
  refreshBilling: () => Promise<void>;
  /** Clear `justActivatedTrial` and bump `billingRefreshKey`. */
  acknowledgeTrial: () => void;
  /** Extend the active trial by 7 days via the metering service. */
  extendTrial: () => Promise<void>;
  /** Monotonic counter bumped when billing-derived server state may have shifted. */
  billingRefreshKey: number;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

/** Props for the BillingProvider component. */
interface BillingProviderProps {
  children: React.ReactNode;
}

/**
 * Provides the billing context. Must nest inside `OrgCoreProvider` (it reads
 * the active org from there).
 */
export const BillingProvider: React.FC<BillingProviderProps> = ({ children }) => {
  const { supabase } = useSupabase();
  const { activeOrg, refreshCore, clearJustActivatedTrial } = useOrgCore();

  const [orgBilling, setOrgBilling] = useState<OrgBilling | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);

  /** Stable ref for the supabase client to avoid re-triggering effects. */
  const supabaseRef = useRef(supabase);
  supabaseRef.current = supabase;

  /**
   * Latest-wins guard (A11): the org id of the most recent fetch request.
   * Every `fetchBilling` stamps this with its target org id; a slow response
   * for org A that lands after the user has switched to org B is discarded
   * (its stamp no longer matches), so B can never end up showing A's billing.
   */
  const latestBillingOrgRef = useRef<string | null>(null);

  /**
   * Fetch billing info for an org via the `reconcile_seat_grace` RPC, which
   * self-heals the grace-period state on every invocation and returns the
   * refreshed `org_billing` row. PostgREST may serialise the `SETOF`
   * return as a single object or a single-element array; both are normalised.
   * A missing row is treated as a no-op. Errors clear `orgBilling` so stale
   * state can't leak. A response that arrives after the active org changed is
   * dropped via the latest-wins stamp.
   */
  const fetchBilling = useCallback(async (orgId: string) => {
    latestBillingOrgRef.current = orgId;
    setBillingLoading(true);
    try {
      const { data, error } = await supabaseRef.current.rpc(
        'reconcile_seat_grace',
        { p_org_id: orgId },
      );

      // Drop a stale response: the active org changed while this was in flight.
      if (latestBillingOrgRef.current !== orgId) return;

      if (error) {
        captureException(
          new Error(error.message),
          'BillingProvider.fetchBilling',
          { orgId },
        );
        setOrgBilling(null);
        return;
      }

      const row = Array.isArray(data) ? data[0] ?? null : data ?? null;
      setOrgBilling(row as OrgBilling | null);
    } catch (err) {
      if (latestBillingOrgRef.current !== orgId) return;
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'BillingProvider.fetchBilling',
        { orgId },
      );
      setOrgBilling(null);
    } finally {
      // Only the latest request owns the loading flag; a stale response must
      // not flip it off while the current fetch is still running.
      if (latestBillingOrgRef.current === orgId) {
        setBillingLoading(false);
      }
    }
  }, []);

  /** Re-fetch billing for the active org. */
  const refreshBilling = useCallback(async () => {
    const orgId = activeOrg?.id;
    if (!orgId) return;
    await fetchBilling(orgId);
  }, [activeOrg?.id, fetchBilling]);

  // Re-fetch billing when the active org changes (or is cleared).
  useEffect(() => {
    if (activeOrg?.id) {
      void fetchBilling(activeOrg.id);
    } else {
      setOrgBilling(null);
      setBillingLoading(false);
    }
  }, [activeOrg?.id, fetchBilling]);

  /**
   * Clear the just-activated trial flag AND bump `billingRefreshKey` so the
   * sidebar hooks (credits / plan badge / trial status) re-fetch. Acts as a
   * safety net after the user dismisses the trial-activated success step.
   */
  const acknowledgeTrial = useCallback(() => {
    clearJustActivatedTrial();
    setBillingRefreshKey((k) => k + 1);
  }, [clearJustActivatedTrial]);

  /**
   * Extend the active trial by 7 days via the Pages Function proxy. Refreshes
   * billing and core memberships on success and bumps `billingRefreshKey` so
   * the sidebar chip and billing page card pick up the new `trial_end`.
   */
  const extendTrial = useCallback(async () => {
    if (!activeOrg?.id) {
      throw new ExtendTrialError('No active organization.');
    }
    const {
      data: { session },
    } = await supabaseRef.current.auth.getSession();
    if (!session?.access_token) {
      const err = new ExtendTrialError('Not authenticated');
      captureException(err, 'BillingProvider.extendTrial', { orgId: activeOrg.id });
      throw err;
    }

    let response: Response;
    try {
      response = await fetch('/api/billing/trial/extend', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Organization-ID': activeOrg.id,
        },
      });
    } catch (fetchErr) {
      captureException(
        fetchErr instanceof Error ? fetchErr : new Error(String(fetchErr)),
        'BillingProvider.extendTrial',
        { orgId: activeOrg.id },
      );
      throw new ExtendTrialError('Network error while extending trial');
    }

    if (!response.ok) {
      let errorCode: string | undefined;
      try {
        const body = (await response.clone().json()) as { error?: string };
        errorCode = body?.error;
      } catch {
        // Not JSON, fall through to generic error.
      }

      if (response.status === 409 && errorCode === 'trial_not_active') {
        const err = new TrialNotActiveError();
        captureException(err, 'BillingProvider.extendTrial', {
          orgId: activeOrg.id,
          status: 409,
          errorCode,
        });
        throw err;
      }
      if (response.status === 409 && errorCode === 'already_extended') {
        const err = new TrialAlreadyExtendedError();
        captureException(err, 'BillingProvider.extendTrial', {
          orgId: activeOrg.id,
          status: 409,
          errorCode,
        });
        throw err;
      }

      const err = new ExtendTrialError(
        `Failed to extend trial (${response.status})`,
      );
      captureException(err, 'BillingProvider.extendTrial', {
        orgId: activeOrg.id,
        status: response.status,
        errorCode: errorCode ?? null,
      });
      throw err;
    }

    // Refresh core memberships (in case seat state shifted) and billing so the
    // sidebar chip / billing card update; bump the signal for the Stripe-backed
    // hooks. Replaces the legacy dual refreshKey/billingRefreshKey bump.
    refreshCore();
    void fetchBilling(activeOrg.id);
    setBillingRefreshKey((k) => k + 1);
  }, [activeOrg?.id, refreshCore, fetchBilling]);

  /** Derive grace state from the active org's billing row. */
  const graceState = useMemo<GraceState>(
    () => ({
      endsAt: orgBilling?.grace_period_ends_at ?? null,
      reason: orgBilling?.grace_reason ?? null,
    }),
    [orgBilling?.grace_period_ends_at, orgBilling?.grace_reason],
  );

  const value = useMemo<BillingContextType>(
    () => ({
      orgBilling,
      billingLoading,
      graceState,
      refreshBilling,
      acknowledgeTrial,
      extendTrial,
      billingRefreshKey,
    }),
    [
      orgBilling,
      billingLoading,
      graceState,
      refreshBilling,
      acknowledgeTrial,
      extendTrial,
      billingRefreshKey,
    ],
  );

  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  );
};

/**
 * Access the billing context.
 *
 * @throws Error if called outside `BillingProvider`.
 */
export const useBilling = (): BillingContextType => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

export default BillingProvider;
